import {
  DEFAULT_SVG_VIEWPORT_VIRTUALIZATION_CONFIG,
  StaticSvgSpatialIndex,
  mergeAabbs,
  normalizeAabb
} from './imported_svg_virtualization.mjs';

const DRAWING_TAGS = new Set([
  'path',
  'line',
  'polyline',
  'polygon',
  'rect',
  'circle',
  'ellipse',
  'text',
  'use',
  'image',
  'foreignobject'
]);

const SUPPORT_TAGS = new Set([
  'defs',
  'style',
  'title',
  'desc',
  'metadata'
]);

// `switch` is deliberately not a shell: moving its children out of the
// conditional container changes SVG language/feature selection semantics.
const CONTAINER_TAGS = new Set(['g', 'a']);
const NON_CULLABLE_TAGS = new Set(['text', 'use', 'image', 'foreignobject', 'svg']);
const SHARED_EFFECT_ATTRIBUTES = [
  'clip-path',
  'mask',
  'filter',
  'marker-start',
  'marker-mid',
  'marker-end'
];
const REFERENCE_ATTRIBUTES = [
  'fill',
  'stroke',
  'filter',
  'clip-path',
  'mask',
  'marker-start',
  'marker-mid',
  'marker-end',
  'href',
  'xlink:href'
];
const EXACT_REFERENCE_ATTRIBUTES = new Set([
  'filter', 'clip-path', 'mask', 'marker-start', 'marker-mid', 'marker-end',
  'href', 'xlink:href'
]);
const SVG_URL_REFERENCE_PATTERN = /url\(\s*(['"]?)#([^\s)'";]+)\1\s*\)/gi;

let renderModelSequence = 0;

function nowMs() {
  return Number(globalThis.performance?.now?.()) || Date.now();
}

function elementTag(element) {
  return String(element?.localName || element?.tagName || '').toLowerCase();
}

function elementChildren(element) {
  return Array.from(element?.children || []).filter(Boolean);
}

function attributeValue(element, name) {
  return String(element?.getAttribute?.(name) || '').trim();
}

function inlineStyleProperty(style, property) {
  const match = String(style || '').match(new RegExp(`(?:^|;)\\s*${property}\\s*:\\s*([^;]+)`, 'i'));
  return String(match?.[1] || '').trim();
}

function opacityCreatesGroup(element) {
  const raw = attributeValue(element, 'opacity') || inlineStyleProperty(attributeValue(element, 'style'), 'opacity');
  if (!raw) return false;
  const numeric = Number(raw);
  return !Number.isFinite(numeric) || numeric < 0.999999;
}

function styleCreatesSharedCompositing(element) {
  const style = attributeValue(element, 'style').toLowerCase();
  const blend = inlineStyleProperty(style, 'mix-blend-mode').toLowerCase();
  const isolation = inlineStyleProperty(style, 'isolation').toLowerCase();
  return Boolean((blend && blend !== 'normal') || (isolation && isolation !== 'auto'));
}

function groupMustRemainAtomic(element, hasStylesheet) {
  if (!element) return true;
  if (opacityCreatesGroup(element) || styleCreatesSharedCompositing(element)) return true;
  if (SHARED_EFFECT_ATTRIBUTES.some(name => attributeValue(element, name))) return true;
  // A stylesheet may target an element selector (`g { filter: ... }`) or an
  // id selector, not only a class. Splitting any such container could change
  // group opacity, filters, masks or blending, so keep it atomic.
  if (hasStylesheet) return true;
  return false;
}

function stylesheetChangesSiblingSemantics(root) {
  for (const style of Array.from(root?.querySelectorAll?.('style') || [])) {
    const css = String(style?.textContent || '');
    if (/:(?:nth|first|last|only)-(?:child|of-type)\b|:has\s*\(|:empty\b|[+~]/i.test(css)) return true;
  }
  return false;
}

const VIEWPORT_DEPENDENT_LENGTH = /(?:%|(?:^|[^a-zA-Z])[-+]?(?:\d*\.\d+|\d+\.?)?(?:e[-+]?\d+)?\s*(?:em|ex|ch|rem|vw|vh|vmin|vmax)\b|\b(?:calc|var|min|max|clamp)\s*\()/i;
const GEOMETRY_LENGTH_ATTRIBUTES = new Set([
  'x', 'y', 'x1', 'y1', 'x2', 'y2', 'cx', 'cy', 'r', 'rx', 'ry',
  'width', 'height', 'stroke-width', 'font-size', 'markerwidth',
  'markerheight', 'refx', 'refy'
]);

function elementHasViewportDependentGeometry(element) {
  const pending = [element];
  while (pending.length) {
    const node = pending.pop();
    for (const attribute of Array.from(node?.attributes || [])) {
      const name = String(attribute?.name || '').toLowerCase();
      if ((GEOMETRY_LENGTH_ATTRIBUTES.has(name) || name === 'style')
          && VIEWPORT_DEPENDENT_LENGTH.test(String(attribute?.value || ''))) {
        return true;
      }
    }
    if (elementTag(node) === 'style' && VIEWPORT_DEPENDENT_LENGTH.test(String(node?.textContent || ''))) {
      return true;
    }
    pending.push(...elementChildren(node));
  }
  return false;
}

function collectReferences(element) {
  const refs = new Set();
  const visit = node => {
    for (const name of REFERENCE_ATTRIBUTES) {
      const value = attributeValue(node, name);
      if (!value) continue;
      SVG_URL_REFERENCE_PATTERN.lastIndex = 0;
      for (const match of value.matchAll(SVG_URL_REFERENCE_PATTERN)) refs.add(match[2]);
      if (EXACT_REFERENCE_ATTRIBUTES.has(name)) {
        const exact = value.match(/^#([^\s]+)$/);
        if (exact) refs.add(exact[1]);
      }
    }
    const style = attributeValue(node, 'style');
    SVG_URL_REFERENCE_PATTERN.lastIndex = 0;
    for (const match of style.matchAll(SVG_URL_REFERENCE_PATTERN)) refs.add(match[2]);
    for (const child of elementChildren(node)) visit(child);
  };
  visit(element);
  return [...refs];
}

function attributeContext(element) {
  const names = [
    'transform', 'class', 'style', 'fill', 'stroke', 'stroke-width',
    'stroke-linejoin', 'stroke-miterlimit', 'fill-rule', 'clip-rule',
    'vector-effect', 'opacity'
  ];
  const result = {};
  for (const name of names) {
    const value = attributeValue(element, name);
    if (value) result[name] = value;
  }
  return result;
}

function fallbackRootBounds(root) {
  const values = attributeValue(root, 'viewBox').split(/[\s,]+/).map(Number);
  if (values.length >= 4 && values.slice(0, 4).every(Number.isFinite) && values[2] > 0 && values[3] > 0) {
    return {
      minX: values[0],
      minZ: values[1],
      maxX: values[0] + values[2],
      maxZ: values[1] + values[3]
    };
  }
  const width = Number.parseFloat(attributeValue(root, 'width'));
  const height = Number.parseFloat(attributeValue(root, 'height'));
  if (Number.isFinite(width) && width > 0 && Number.isFinite(height) && height > 0) {
    return { minX: 0, minZ: 0, maxX: width, maxZ: height };
  }
  return null;
}

function transformedBounds(bounds, matrix) {
  const rect = normalizeAabb(bounds);
  if (!rect || !matrix) return rect;
  const points = [
    [rect.minX, rect.minZ],
    [rect.maxX, rect.minZ],
    [rect.maxX, rect.maxZ],
    [rect.minX, rect.maxZ]
  ].map(([x, z]) => ({
    x: matrix.a * x + matrix.c * z + matrix.e,
    z: matrix.b * x + matrix.d * z + matrix.f
  }));
  return {
    minX: Math.min(...points.map(point => point.x)),
    minZ: Math.min(...points.map(point => point.z)),
    maxX: Math.max(...points.map(point => point.x)),
    maxZ: Math.max(...points.map(point => point.z))
  };
}

const IDENTITY_MATRIX = Object.freeze({ a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 });

function multiplyMatrices(left, right) {
  if (!left || !right) return null;
  return {
    a: left.a * right.a + left.c * right.b,
    b: left.b * right.a + left.d * right.b,
    c: left.a * right.c + left.c * right.d,
    d: left.b * right.c + left.d * right.d,
    e: left.a * right.e + left.c * right.f + left.e,
    f: left.b * right.e + left.d * right.f + left.f
  };
}

function parseSvgTransform(transformValue) {
  const raw = String(transformValue || '').trim();
  if (!raw) return { ...IDENTITY_MATRIX };
  let matrix = { ...IDENTITY_MATRIX };
  let matchedLength = 0;
  const expression = /([a-z]+)\s*\(([^)]*)\)/gi;
  let match;
  while ((match = expression.exec(raw))) {
    const gap = raw.slice(matchedLength, match.index).replace(/[\s,]+/g, '');
    if (gap) return null;
    matchedLength = expression.lastIndex;
    const name = match[1].toLowerCase();
    const values = match[2].trim().split(/[\s,]+/).filter(Boolean).map(Number);
    if (values.some(value => !Number.isFinite(value))) return null;
    let next = null;
    if (name === 'matrix' && values.length >= 6) {
      next = { a: values[0], b: values[1], c: values[2], d: values[3], e: values[4], f: values[5] };
    } else if (name === 'translate' && values.length >= 1) {
      next = { a: 1, b: 0, c: 0, d: 1, e: values[0], f: values[1] || 0 };
    } else if (name === 'scale' && values.length >= 1) {
      const sy = Number.isFinite(values[1]) ? values[1] : values[0];
      next = { a: values[0], b: 0, c: 0, d: sy, e: 0, f: 0 };
    } else if (name === 'rotate' && values.length >= 1) {
      const radians = values[0] * Math.PI / 180;
      const rotate = { a: Math.cos(radians), b: Math.sin(radians), c: -Math.sin(radians), d: Math.cos(radians), e: 0, f: 0 };
      if (values.length >= 3) {
        next = multiplyMatrices(
          multiplyMatrices({ a: 1, b: 0, c: 0, d: 1, e: values[1], f: values[2] }, rotate),
          { a: 1, b: 0, c: 0, d: 1, e: -values[1], f: -values[2] }
        );
      } else {
        next = rotate;
      }
    } else if ((name === 'skewx' || name === 'skewy') && values.length >= 1) {
      const tangent = Math.tan(values[0] * Math.PI / 180);
      next = name === 'skewx'
        ? { a: 1, b: 0, c: tangent, d: 1, e: 0, f: 0 }
        : { a: 1, b: tangent, c: 0, d: 1, e: 0, f: 0 };
    }
    if (!next) return null;
    matrix = multiplyMatrices(matrix, next);
  }
  if (raw.slice(matchedLength).replace(/[\s,]+/g, '')) return null;
  return matrix;
}

function numericAttribute(element, name, fallback = NaN) {
  const value = Number(attributeValue(element, name));
  return Number.isFinite(value) ? value : fallback;
}

function mergePoint(bounds, x, z) {
  if (!Number.isFinite(x) || !Number.isFinite(z)) return bounds;
  return mergeAabbs(bounds, { minX: x, minZ: z, maxX: x, maxZ: z });
}

function parsePathBoundsAnalytically(value) {
  const tokens = String(value || '').match(/[a-zA-Z]|[-+]?(?:\d*\.\d+|\d+\.?)(?:e[-+]?\d+)?/gi) || [];
  if (!tokens.length) return null;
  let index = 0;
  let command = '';
  let x = 0;
  let z = 0;
  let startX = 0;
  let startZ = 0;
  let lastCubicControl = null;
  let lastQuadraticControl = null;
  let bounds = null;
  const isCommand = token => /^[a-zA-Z]$/.test(token);
  const read = () => Number(tokens[index++]);
  const add = (px, pz) => { bounds = mergePoint(bounds, px, pz); };
  const addExpanded = (px, pz, padding) => {
    const extent = Math.max(0, Number(padding) || 0);
    bounds = mergeAabbs(bounds, {
      minX: px - extent,
      minZ: pz - extent,
      maxX: px + extent,
      maxZ: pz + extent
    });
  };
  const absolutePair = relative => {
    const px = read();
    const pz = read();
    return { x: relative ? x + px : px, z: relative ? z + pz : pz };
  };

  while (index < tokens.length) {
    if (isCommand(tokens[index])) command = tokens[index++];
    if (!command) return null;
    const relative = command === command.toLowerCase();
    const type = command.toUpperCase();
    if (type === 'Z') {
      x = startX;
      z = startZ;
      add(x, z);
      lastCubicControl = null;
      lastQuadraticControl = null;
      command = '';
      continue;
    }
    if (type === 'H' || type === 'V') {
      while (index < tokens.length && !isCommand(tokens[index])) {
        const coordinate = read();
        if (type === 'H') x = relative ? x + coordinate : coordinate;
        else z = relative ? z + coordinate : coordinate;
        add(x, z);
      }
      lastCubicControl = null;
      lastQuadraticControl = null;
      continue;
    }
    if (type === 'A') {
      while (index < tokens.length && !isCommand(tokens[index])) {
        if (index + 7 > tokens.length) return null;
        let radiusX = Math.abs(read());
        let radiusZ = Math.abs(read());
        const rotation = read();
        const largeArc = read();
        const sweep = read();
        const endRawX = read();
        const endRawZ = read();
        if (![radiusX, radiusZ, rotation, largeArc, sweep, endRawX, endRawZ].every(Number.isFinite)
            || (largeArc !== 0 && largeArc !== 1)
            || (sweep !== 0 && sweep !== 1)) {
          return null;
        }
        const end = {
          x: relative ? x + endRawX : endRawX,
          z: relative ? z + endRawZ : endRawZ
        };
        if (radiusX > 0 && radiusZ > 0 && (end.x !== x || end.z !== z)) {
          // SVG expands radii when the requested ellipse cannot connect both
          // end points. Reproduce that correction and then use a deliberately
          // conservative endpoint-centred envelope. Every arc point is at
          // most 2 * hypot(rx, ry) from either endpoint, independent of axis
          // rotation and arc flags. This avoids thousands of getBBox() calls
          // for CAD paths while guaranteeing no false-negative culling.
          const radians = rotation * Math.PI / 180;
          const halfDx = (x - end.x) / 2;
          const halfDz = (z - end.z) / 2;
          const rotatedX = Math.cos(radians) * halfDx + Math.sin(radians) * halfDz;
          const rotatedZ = -Math.sin(radians) * halfDx + Math.cos(radians) * halfDz;
          const correction = (rotatedX * rotatedX) / (radiusX * radiusX)
            + (rotatedZ * rotatedZ) / (radiusZ * radiusZ);
          if (correction > 1) {
            const factor = Math.sqrt(correction);
            radiusX *= factor;
            radiusZ *= factor;
          }
          const conservativeRadius = 2 * Math.hypot(radiusX, radiusZ);
          addExpanded(x, z, conservativeRadius);
          addExpanded(end.x, end.z, conservativeRadius);
        } else {
          add(end.x, end.z);
        }
        x = end.x;
        z = end.z;
        lastCubicControl = null;
        lastQuadraticControl = null;
      }
      continue;
    }
    const parameterCounts = { M: 2, L: 2, C: 6, S: 4, Q: 4, T: 2 };
    const parameterCount = parameterCounts[type];
    if (!parameterCount) return null;
    let firstMove = type === 'M';
    while (index < tokens.length && !isCommand(tokens[index])) {
      if (index + parameterCount > tokens.length) return null;
      if (type === 'M' || type === 'L' || type === 'T') {
        let control = null;
        if (type === 'T') {
          control = lastQuadraticControl
            ? { x: 2 * x - lastQuadraticControl.x, z: 2 * z - lastQuadraticControl.z }
            : { x, z };
          add(control.x, control.z);
        }
        const end = absolutePair(relative);
        x = end.x;
        z = end.z;
        add(x, z);
        if (type === 'M' && firstMove) {
          startX = x;
          startZ = z;
          firstMove = false;
        }
        lastQuadraticControl = type === 'T' ? control : null;
        lastCubicControl = null;
      } else if (type === 'C') {
        const c1 = absolutePair(relative);
        const c2 = absolutePair(relative);
        const end = absolutePair(relative);
        add(c1.x, c1.z);
        add(c2.x, c2.z);
        add(end.x, end.z);
        x = end.x;
        z = end.z;
        lastCubicControl = c2;
        lastQuadraticControl = null;
      } else if (type === 'S') {
        const reflected = lastCubicControl
          ? { x: 2 * x - lastCubicControl.x, z: 2 * z - lastCubicControl.z }
          : { x, z };
        const c2 = absolutePair(relative);
        const end = absolutePair(relative);
        add(reflected.x, reflected.z);
        add(c2.x, c2.z);
        add(end.x, end.z);
        x = end.x;
        z = end.z;
        lastCubicControl = c2;
        lastQuadraticControl = null;
      } else if (type === 'Q') {
        const control = absolutePair(relative);
        const end = absolutePair(relative);
        add(control.x, control.z);
        add(end.x, end.z);
        x = end.x;
        z = end.z;
        lastQuadraticControl = control;
        lastCubicControl = null;
      }
      if (type === 'M') command = relative ? 'l' : 'L';
    }
  }
  return bounds;
}

function primitiveLocalBounds(element, tag) {
  if (tag === 'rect') {
    const x = numericAttribute(element, 'x', 0);
    const z = numericAttribute(element, 'y', 0);
    const width = numericAttribute(element, 'width');
    const depth = numericAttribute(element, 'height');
    return width >= 0 && depth >= 0 ? { minX: x, minZ: z, maxX: x + width, maxZ: z + depth } : null;
  }
  if (tag === 'line') {
    const x1 = numericAttribute(element, 'x1', 0);
    const z1 = numericAttribute(element, 'y1', 0);
    const x2 = numericAttribute(element, 'x2', 0);
    const z2 = numericAttribute(element, 'y2', 0);
    return { minX: Math.min(x1, x2), minZ: Math.min(z1, z2), maxX: Math.max(x1, x2), maxZ: Math.max(z1, z2) };
  }
  if (tag === 'circle' || tag === 'ellipse') {
    const cx = numericAttribute(element, 'cx', 0);
    const cz = numericAttribute(element, 'cy', 0);
    const rx = numericAttribute(element, tag === 'circle' ? 'r' : 'rx');
    const rz = numericAttribute(element, tag === 'circle' ? 'r' : 'ry');
    return rx >= 0 && rz >= 0 ? { minX: cx - rx, minZ: cz - rz, maxX: cx + rx, maxZ: cz + rz } : null;
  }
  if (tag === 'polyline' || tag === 'polygon') {
    const numbers = attributeValue(element, 'points').match(/[-+]?(?:\d*\.\d+|\d+\.?)(?:e[-+]?\d+)?/gi)?.map(Number) || [];
    if (numbers.length < 2 || numbers.length % 2 !== 0) return null;
    let bounds = null;
    for (let index = 0; index < numbers.length; index += 2) bounds = mergePoint(bounds, numbers[index], numbers[index + 1]);
    return bounds;
  }
  if (tag === 'path') return parsePathBoundsAnalytically(attributeValue(element, 'd'));
  return null;
}

function parsedSvgUserLength(value) {
  const raw = String(value || '').trim();
  if (!raw) return 0;
  const match = raw.match(/^([-+]?(?:\d*\.\d+|\d+\.?)(?:e[-+]?\d+)?)\s*(px)?$/i);
  if (!match) return null;
  const numeric = Number(match[1]);
  return Number.isFinite(numeric) ? Math.max(0, numeric) : null;
}

function inheritedStyleProperty(styleContext, property) {
  for (let index = styleContext.length - 1; index >= 0; index -= 1) {
    const context = styleContext[index] || {};
    const raw = context[property] || inlineStyleProperty(context.style, property);
    if (raw) return raw;
  }
  return '';
}

function inheritedStrokeExtent(styleContext) {
  const width = parsedSvgUserLength(inheritedStyleProperty(styleContext, 'stroke-width'));
  if (width === null) return null;
  const rawLimit = inheritedStyleProperty(styleContext, 'stroke-miterlimit');
  const parsedLimit = rawLimit ? Number(rawLimit) : 4;
  if (!Number.isFinite(parsedLimit) || parsedLimit < 1) return null;
  // Applying the miter limit for every join is conservative for round/bevel
  // joins and also covers inherited/default `stroke-linejoin: miter`.
  return width * Math.max(1, parsedLimit) / 2;
}

function styleContextHasNonScalingStroke(styleContext) {
  for (let index = styleContext.length - 1; index >= 0; index -= 1) {
    const context = styleContext[index] || {};
    const value = context['vector-effect'] || inlineStyleProperty(context.style, 'vector-effect');
    if (value) return String(value).toLowerCase() === 'non-scaling-stroke';
  }
  return false;
}

function nonScalingStrokeExtentForElement(element, ancestorStyleContext = []) {
  let maximum = 0;
  let unresolved = false;
  const walk = (node, context) => {
    const nextContext = [...context, attributeContext(node)];
    if (styleContextHasNonScalingStroke(nextContext)) {
      const extent = inheritedStrokeExtent(nextContext);
      if (extent === null) unresolved = true;
      else maximum = Math.max(maximum, extent);
    }
    for (const child of elementChildren(node)) walk(child, nextContext);
  };
  // The descriptor context already contains the unit element itself. Start
  // descendants with it, and evaluate the unit without duplicating context.
  if (styleContextHasNonScalingStroke(ancestorStyleContext)) {
    const extent = inheritedStrokeExtent(ancestorStyleContext);
    if (extent === null) unresolved = true;
    else maximum = Math.max(maximum, extent);
  }
  for (const child of elementChildren(element)) walk(child, ancestorStyleContext);
  return unresolved ? null : maximum;
}

function matrixMaximumScale(matrix) {
  if (!matrix) return 1;
  const sumSquares = matrix.a * matrix.a + matrix.b * matrix.b
    + matrix.c * matrix.c + matrix.d * matrix.d;
  const determinant = matrix.a * matrix.d - matrix.b * matrix.c;
  const discriminant = Math.max(0, sumSquares * sumSquares - 4 * determinant * determinant);
  return Math.max(1, Math.sqrt(Math.max(0, (sumSquares + Math.sqrt(discriminant)) / 2)));
}

function unitAnalyticBounds(unit, hasStylesheet) {
  if (!unit || unit.atomic || !unit.matrix) return null;
  // A stylesheet may change stroke width, markers or other painted extents
  // through selectors which are not represented by the attribute context.
  // Measure those units once in the browser instead of risking a too-small
  // analytic box. Attribute-only CAD exports keep the faster analytic path.
  if (hasStylesheet) return null;
  const local = primitiveLocalBounds(unit.sourceElement, unit.elementType);
  if (!local) return null;
  const strokeExtent = inheritedStrokeExtent(unit.styleContext);
  if (strokeExtent === null) return null;
  const transformed = transformedBounds(local, unit.matrix);
  // Largest singular value remains conservative for arbitrary shear while
  // avoiding the avoidable sqrt(2) inflation of a raw Frobenius norm.
  const matrixScale = matrixMaximumScale(unit.matrix);
  const padding = Math.max(
    0.01,
    unit.nonScalingStrokeExtent > 0 ? 0 : strokeExtent * matrixScale
  );
  return {
    minX: transformed.minX - padding,
    minZ: transformed.minZ - padding,
    maxX: transformed.maxX + padding,
    maxZ: transformed.maxZ + padding
  };
}

function expandMeasuredStroke(bounds, element, matrix) {
  const rect = normalizeAabb(bounds);
  if (!rect) return null;
  let strokeWidth = parsedSvgUserLength(attributeValue(element, 'stroke-width'));
  let miterLimit = Number(attributeValue(element, 'stroke-miterlimit')) || 4;
  try {
    const computed = globalThis.getComputedStyle?.(element);
    const computedWidth = parsedSvgUserLength(computed?.strokeWidth);
    if (computedWidth !== null) strokeWidth = Math.max(strokeWidth ?? 0, computedWidth);
    const computedMiterLimit = Number(computed?.strokeMiterlimit);
    if (Number.isFinite(computedMiterLimit)) miterLimit = Math.max(miterLimit, computedMiterLimit);
  } catch (_error) {
    // Detached/fake DOM: the measured geometry remains usable; the fixed
    // epsilon below plus viewport overscan keeps boundary queries conservative.
  }
  const matrixScale = matrixMaximumScale(matrix);
  const padding = Math.max(0.01, (strokeWidth ?? 0) * Math.max(1, miterLimit) * matrixScale / 2);
  return {
    minX: rect.minX - padding,
    minZ: rect.minZ - padding,
    maxX: rect.maxX + padding,
    maxZ: rect.maxZ + padding
  };
}

function measuredElementBounds(element) {
  if (!element || typeof element.getBBox !== 'function') return null;
  let bbox;
  try {
    // SVG 2 getBBox options are supported by current Chromium. Browsers which
    // ignore the argument still return the fill geometry; expandMeasuredStroke
    // adds the computed stroke conservatively afterwards.
    bbox = element.getBBox({ fill: true, stroke: true, markers: true, clipped: false });
  } catch (_error) {
    try {
      bbox = element.getBBox();
    } catch (_nestedError) {
      return null;
    }
  }
  if (!bbox || ![bbox.x, bbox.y, bbox.width, bbox.height].every(Number.isFinite)) return null;
  const matrix = element.getCTM?.() || { a: 1, b: 0, c: 0, d: 1, e: 0, f: 0 };
  return expandMeasuredStroke(transformedBounds({
    minX: bbox.x,
    minZ: bbox.y,
    maxX: bbox.x + bbox.width,
    maxZ: bbox.y + bbox.height
  }, matrix), element, matrix);
}

function measuredNonScalingStrokeExtent(element) {
  if (!element || typeof globalThis.getComputedStyle !== 'function') return 0;
  let maximum = 0;
  const pending = [element];
  while (pending.length) {
    const node = pending.pop();
    try {
      const computed = globalThis.getComputedStyle(node);
      if (String(computed?.vectorEffect || '').toLowerCase() === 'non-scaling-stroke') {
        const width = parsedSvgUserLength(computed?.strokeWidth);
        const miterLimit = Number(computed?.strokeMiterlimit) || 4;
        if (width === null || !Number.isFinite(miterLimit)) return null;
        maximum = Math.max(maximum, width * Math.max(1, miterLimit) / 2);
      }
    } catch (_error) {
      return null;
    }
    pending.push(...elementChildren(node));
  }
  return maximum;
}

function createMeasurementTree(root, units, rewriteIds) {
  if (!globalThis.document?.createElementNS || !globalThis.document?.body || typeof root?.cloneNode !== 'function') return null;
  const cloneRoot = root.cloneNode(true);
  const sourceUnitByElement = new Map(units.map(unit => [unit.sourceElement, unit]));
  const cloneByUnitId = new Map();
  const pairWalk = (source, clone) => {
    const unit = sourceUnitByElement.get(source);
    if (unit) cloneByUnitId.set(unit.id, clone);
    const sourceChildren = elementChildren(source);
    const cloneChildren = elementChildren(clone);
    for (let index = 0; index < Math.min(sourceChildren.length, cloneChildren.length); index += 1) {
      pairWalk(sourceChildren[index], cloneChildren[index]);
    }
  };
  pairWalk(root, cloneRoot);
  rewriteIds?.(cloneRoot, `ks-svg-measure-${renderModelSequence}`);
  const measurementSvg = globalThis.document.createElementNS('http://www.w3.org/2000/svg', 'svg');
  measurementSvg.setAttribute('aria-hidden', 'true');
  measurementSvg.setAttribute('width', '1');
  measurementSvg.setAttribute('height', '1');
  measurementSvg.setAttribute('overflow', 'visible');
  measurementSvg.style.cssText = 'position:fixed;left:-100000px;top:-100000px;width:1px;height:1px;overflow:visible;visibility:hidden;pointer-events:none;contain:strict';
  // The production renderer reparents root children into an asset <g>. Mirror
  // that hierarchy here so selector inheritance and CTMs match its local
  // coordinate system; root viewBox/percentage-dependent constructs are
  // classified as non-cullable separately.
  const measurementLayer = globalThis.document.createElementNS('http://www.w3.org/2000/svg', 'g');
  for (const child of Array.from(cloneRoot.childNodes || [])) measurementLayer.appendChild(child);
  measurementSvg.appendChild(measurementLayer);
  globalThis.document.body.appendChild(measurementSvg);
  return { measurementSvg, cloneByUnitId };
}

function semanticUnitIsCullable(element, tag) {
  if (NON_CULLABLE_TAGS.has(tag)) return false;
  if (elementHasViewportDependentGeometry(element)) return false;
  const pending = [element];
  while (pending.length) {
    const node = pending.pop();
    if (SHARED_EFFECT_ATTRIBUTES.some(name => attributeValue(node, name))) return false;
    if (styleCreatesSharedCompositing(node)) return false;
    const nodeTag = elementTag(node);
    if (NON_CULLABLE_TAGS.has(nodeTag)) return false;
    pending.push(...elementChildren(node));
  }
  return true;
}

function countSvgTree(root) {
  let totalDomNodes = 0;
  let drawingElements = 0;
  const walk = (node, insideSupport = false) => {
    for (const child of elementChildren(node)) {
      totalDomNodes += 1;
      const tag = elementTag(child);
      const support = insideSupport || SUPPORT_TAGS.has(tag);
      if (!support && DRAWING_TAGS.has(tag)) drawingElements += 1;
      walk(child, support);
    }
  };
  walk(root, false);
  return { totalDomNodes, drawingElements };
}

function buildDescriptorTree(root) {
  const hasStylesheet = Boolean(root?.querySelector?.('style'));
  const units = [];
  const shells = [];
  let sourceOrder = 0;
  let shellSequence = 0;

  const walk = (parent, parentKey, sourcePath, transformContext, styleContext, parentMatrix) => {
    const descriptors = [];
    elementChildren(parent).forEach((child, siblingIndex) => {
      const tag = elementTag(child);
      const path = [...sourcePath, siblingIndex];
      if (SUPPORT_TAGS.has(tag)) {
        descriptors.push({ type: 'support', sourceElement: child, siblingIndex, sourcePath: path });
        return;
      }
      const nextTransformContext = attributeValue(child, 'transform')
        ? [...transformContext, attributeValue(child, 'transform')]
        : transformContext;
      const nextStyleContext = [...styleContext, attributeContext(child)];
      const childTransform = parseSvgTransform(attributeValue(child, 'transform'));
      const nextMatrix = multiplyMatrices(parentMatrix, childTransform);
      if (CONTAINER_TAGS.has(tag) && !groupMustRemainAtomic(child, hasStylesheet)) {
        const key = `shell-${shellSequence += 1}`;
        const descriptor = {
          type: 'shell',
          key,
          sourceElement: child,
          siblingIndex,
          sourcePath: path,
          parentKey,
          children: walk(child, key, path, nextTransformContext, nextStyleContext, nextMatrix)
        };
        shells.push(descriptor);
        descriptors.push(descriptor);
        return;
      }
      const order = sourceOrder += 1;
      const nonScalingStrokeExtent = nonScalingStrokeExtentForElement(child, nextStyleContext);
      const safeToVirtualize = semanticUnitIsCullable(child, tag)
        && nonScalingStrokeExtent !== null;
      const unit = {
        id: `svg-unit-${order.toString(36)}`,
        order,
        siblingIndex,
        sourcePath: path,
        parentKey,
        elementType: tag || 'unknown',
        sourceElement: child,
        transformContext: nextTransformContext,
        styleContext: nextStyleContext,
        matrix: nextMatrix,
        references: collectReferences(child),
        atomic: elementChildren(child).length > 0,
        nonScalingStrokeExtent: nonScalingStrokeExtent || 0,
        safeToVirtualize,
        cullable: safeToVirtualize,
        bounds: null
      };
      units.push(unit);
      descriptors.push({ type: 'unit', unit, siblingIndex, sourcePath: path });
    });
    return descriptors;
  };

  return {
    descriptors: walk(root, 'root', [], [], [], { ...IDENTITY_MATRIX }),
    units,
    shells,
    hasStylesheet
  };
}

function buildImportedSvgRenderModel(root, options = {}) {
  const startedAt = nowMs();
  const counts = countSvgTree(root);
  const minimum = Number(options.minDrawingElements)
    || DEFAULT_SVG_VIEWPORT_VIRTUALIZATION_CONFIG.minDrawingElements;
  const base = {
    id: `imported-svg-model-${renderModelSequence += 1}`,
    root,
    ...counts,
    threshold: minimum,
    virtualizable: false,
    fallbackReason: '',
    descriptors: [],
    units: [],
    shells: [],
    index: null,
    contentBounds: fallbackRootBounds(root),
    assetCullable: false,
    hasNonCullableUnits: false,
    maxNonScalingStrokeExtent: 0,
    boundsMs: 0,
    indexMs: 0
  };
  if (counts.drawingElements < minimum) {
    base.fallbackReason = 'below-threshold';
    base.boundsMs = nowMs() - startedAt;
    return base;
  }
  if (stylesheetChangesSiblingSemantics(root)) {
    base.fallbackReason = 'structural-css-selectors';
    base.boundsMs = nowMs() - startedAt;
    return base;
  }
  const viewportDependentStylesheet = Array.from(root?.querySelectorAll?.('style') || [])
    .some(style => VIEWPORT_DEPENDENT_LENGTH.test(String(style?.textContent || '')));
  if (viewportDependentStylesheet) {
    base.fallbackReason = 'viewport-dependent-stylesheet';
    base.boundsMs = nowMs() - startedAt;
    return base;
  }

  try {
    const tree = buildDescriptorTree(root);
    base.descriptors = tree.descriptors;
    base.units = tree.units;
    base.shells = tree.shells;
    for (const unit of base.units) {
      unit.bounds = unitAnalyticBounds(unit, tree.hasStylesheet);
    }
    const unresolvedUnits = base.units.filter(unit => !unit.bounds);
    const measurement = unresolvedUnits.length
      ? createMeasurementTree(root, unresolvedUnits, options.rewriteIds)
      : null;
    let measuredBounds = null;
    try {
      for (const unit of base.units) {
        if (!unit.bounds) {
          const clone = measurement?.cloneByUnitId.get(unit.id);
          unit.bounds = measuredElementBounds(clone);
          const measuredNonScalingExtent = measuredNonScalingStrokeExtent(clone);
          if (measuredNonScalingExtent === null) unit.cullable = false;
          else unit.nonScalingStrokeExtent = Math.max(
            Number(unit.nonScalingStrokeExtent) || 0,
            measuredNonScalingExtent
          );
        }
        if (!unit.bounds) unit.cullable = false;
        if (unit.bounds) measuredBounds = mergeAabbs(measuredBounds, unit.bounds);
      }
    } finally {
      measurement?.measurementSvg?.remove?.();
    }
    base.contentBounds = measuredBounds || base.contentBounds;
    base.maxNonScalingStrokeExtent = base.units.reduce(
      (maximum, unit) => Math.max(maximum, Number(unit.nonScalingStrokeExtent) || 0),
      0
    );
    base.boundsMs = nowMs() - startedAt;

    const cullableCount = base.units.filter(unit => unit.cullable !== false && unit.bounds).length;
    base.hasNonCullableUnits = base.units.some(unit => unit.cullable === false);
    // A single unsafe unit (text awaiting a font, a filter, marker, nested
    // SVG, unresolved geometry, ...) disables only the coarse whole-asset
    // rejection. Safe sibling units are still internally virtualized, while
    // the unsafe unit remains mounted through the index's always-list.
    base.assetCullable = !base.hasNonCullableUnits && Boolean(base.contentBounds);
    // If almost everything is an unsupported atomic construct, a virtual DOM
    // would only add bookkeeping without reducing the heavy node count.
    if (!base.units.length || cullableCount < Math.min(minimum, Math.ceil(base.units.length * 0.1))) {
      base.fallbackReason = measurement ? 'insufficient-cullable-units' : 'bbox-measurement-unavailable';
      return base;
    }

    const indexStartedAt = nowMs();
    base.index = new StaticSvgSpatialIndex(base.units, options);
    base.indexMs = nowMs() - indexStartedAt;
    base.virtualizable = true;
    return base;
  } catch (error) {
    base.fallbackReason = `model-error:${String(error?.message || error)}`;
    base.boundsMs = nowMs() - startedAt;
    if (options.development === true) console.warn('[KS SVG] Virtualisierungsmodell fehlgeschlagen; Vollrender-Fallback.', error);
    return base;
  }
}

function contentBoundsFromRenderModel(model, fallback = null) {
  const bounds = normalizeAabb(model?.contentBounds);
  if (!bounds) return fallback;
  const width = bounds.maxX - bounds.minX;
  const depth = bounds.maxZ - bounds.minZ;
  return width > 0 && depth > 0
    ? { minX: bounds.minX, minZ: bounds.minZ, width, depth }
    : fallback;
}

export {
  DRAWING_TAGS,
  SUPPORT_TAGS,
  buildImportedSvgRenderModel,
  contentBoundsFromRenderModel,
  countSvgTree,
  groupMustRemainAtomic,
  stylesheetChangesSiblingSemantics
};
